"use client";

import { useState, useEffect } from "react";
import { MapPin, Star } from "lucide-react";
import { googleMapHandler, googleMapImageHandler } from "../../services/googlemap";
import { fetchMasterCities } from "../../services/locationServices";

const tabs = [
  {
    id: "Restaurants",
    label: "Restaurants",
    icon: "🍽️",
    imagePath: "/landing/discover/restaurants.png",
    type: "restaurant",
    keyword: "indian",
    title: "Restaurants",
  },
  {
    id: "Groceries",
    label: "Groceries",
    icon: null,
    imagePath: null,
    type: "supermarket|grocery_or_supermarket|food|store",
    keyword: "indian",
    title: "Groceries",
  },
  {
    id: "Professionals",
    label: "Professionals",
    icon: "👔",
    imagePath: "/landing/discover/professional.png",
    type: "doctor",
    keyword: "doctors+near+me+that+speak-desi+language",
    title: "Professionals",
  },
  {
    id: "Small Business",
    label: "Small Business",
    icon: "🏪",
    imagePath: "/landing/discover/smallbusiness.png",
    type: "florist|convenience_store|liquor_store|pet_store|clothing_store|jewelry_store|book_store|hardware_store|pharmacy|beauty_salon|hair_care|laundry|shoe_store",
    keyword: "",
    title: "Small Businesses",
  },
];

const getDistanceFromLatLonInKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return (R * c).toFixed(2);
};

const DiscoverSection = () => {
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [mapData, setMapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState(null);
  const [displayCount, setDisplayCount] = useState(8);
  const [businessImages, setBusinessImages] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  const radius = 5000;

  useEffect(() => {
    const getLocationFromAPI = async () => {
      try {
        const geohash = localStorage.getItem("geohash") || "9v6m";
        const locationResponse = await fetchMasterCities({ geohash });
        
        if (locationResponse?.data?.[0]) {
          const cityData = locationResponse.data[0];
          
          if (cityData.lat && cityData.long) {
            setLocation({
              latitude: parseFloat(cityData.lat),
              longitude: parseFloat(cityData.long),
            });
            return;
          }
          
          if (cityData.latitude && cityData.longitude) {
            setLocation({
              latitude: parseFloat(cityData.latitude),
              longitude: parseFloat(cityData.longitude),
            });
            return;
          }
          
          if (cityData.lat && cityData.lng) {
            setLocation({
              latitude: parseFloat(cityData.lat),
              longitude: parseFloat(cityData.lng),
            });
            return;
          }
        }
        
        console.warn("No coordinates found in API response for geohash:", geohash);
      } catch (error) {
        console.error("Error fetching location from API:", error);
      }
    };

    getLocationFromAPI();

    const handleStorageChange = (e) => {
      if (e.key === "geohash") {
        getLocationFromAPI();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    
    const handleCustomStorageChange = () => {
      getLocationFromAPI();
    };
    
    window.addEventListener("geohashChanged", handleCustomStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("geohashChanged", handleCustomStorageChange);
    };
  }, []);

  const fetchBusinessImages = async (businesses) => {
    const imagePromises = businesses.map(async (business) => {
      const placeId = business.place_id;
      const photoReference = business.photos?.[0]?.photo_reference;

      if (!photoReference) return { placeId, imageUrl: null };

      try {
        setLoadingImages(prev => ({ ...prev, [placeId]: true }));
        const imageUrl = await googleMapImageHandler({ photo_reference:photoReference });

        return { placeId, imageUrl };
      } catch (err) {
        return { placeId, imageUrl: null };
      } finally {
        setLoadingImages(prev => ({ ...prev, [placeId]: false }));
      }
    });

    const results = await Promise.all(imagePromises);
    const imagesMap = {};
    results.forEach(({ placeId, imageUrl }) => {
      if (imageUrl) imagesMap[placeId] = imageUrl;
    });
    
    setBusinessImages(prev => ({ ...prev, ...imagesMap }));
  };

  useEffect(() => {
    const fetchMapData = async () => {
      if (!location?.latitude || !location?.longitude) {
        return;
      }

      try {
        setLoading(true);
        
        const geohash = localStorage.getItem("geohash") || "9v6m";
        const payload = {
          latitude: location.latitude,
          longitude: location.longitude,
          radius,
          type: activeTab.type,
          keyword: activeTab.keyword,
          title: activeTab.title,
          geohash: geohash,
        };

        const data = await googleMapHandler(payload);
        const dataWithDistance = (data || []).map((business) => {
          const lat = business.geometry?.location?.lat;
          const lng = business.geometry?.location?.lng;

          if (lat && lng) {
            return {
              ...business,
              distance: getDistanceFromLatLonInKm(
                location.latitude,
                location.longitude,
                lat,
                lng
              ),
            };
          }
          return {
            ...business,
            distance: "N/A",
          };
        });
        
        setMapData(dataWithDistance);
        setDisplayCount(7);
        
        if (dataWithDistance.length > 0) {
          fetchBusinessImages(dataWithDistance);
        }
      } catch (err) {
        console.error("Error fetching map data:", err);
        setMapData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMapData();
  }, [activeTab, location]);

  const currentData = mapData.slice(0, displayCount) || [];

  const handleLoadMore = () => {
    setDisplayCount((prev) => Math.min(prev + 8, mapData.length));
  };

  const FeaturedCard = ({ business }) => {
    const imageUrl = businessImages[business.place_id];
    const isLoadingImage = loadingImages[business.place_id];

    return (
      <div className="bg-white rounded-b-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden flex flex-col border-b-4" style={{ borderBottomColor: "#7B189F", height: '100%' }}>
        <div className="relative bg-gradient-to-br from-orange-400 to-red-500 flex-shrink-0" style={{ 
          borderTopLeftRadius: 0, 
          borderTopRightRadius: 0,
          height: 'clamp(200px, 17.36vw, 280px)',
          minHeight: '200px',
          maxHeight: '280px'
        }}>
          {business.discount?.length > 0 && (
            <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium z-10">
              {business.discount.map((offer, index) => (
                <div key={index}>
                  {offer.title} ({offer.discount_percentage}%)
                </div>
              ))}
            </div>
          )}
          {business.rating && (
            <div className="absolute top-4 right-4 bg-green-500 text-white px-2 py-1 rounded text-xs font-bold flex items-center gap-1 z-10">
              <Star className="w-3 h-3 fill-white" />
              {business.rating}
            </div>
          )}
          
          {isLoadingImage ? (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <div className="w-8 h-8 border-3 border-[#7B189F] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : imageUrl ? (
            <img 
              src={imageUrl} 
              alt={business.name}
              className="w-full h-full object-cover"
              style={{ 
                borderTopLeftRadius: 0, 
                borderTopRightRadius: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
            />
          ) : null}
          
          {!imageUrl && !isLoadingImage && (
            <div className="w-full h-full flex items-center justify-center text-white opacity-50">
              No Image Available
            </div>
          )}
        </div>
        <div className="p-6 flex-shrink-0">
          <h3 className="font-bold text-xl text-gray-800 mb-2">
            {business.name}
          </h3>
          <p className="text-gray-600 text-sm mb-3">{business.category}</p>
          <div className="flex items-center justify-between text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              <span>
                {business.vicinity?.slice(0, 25) +
                  (business.vicinity?.length > 25 ? "…" : "")}
              </span>
            </div>
            <span className="font-medium">{business.distance} km</span>
          </div>
        </div>
      </div>
    );
  };

  const SmallCard = ({ business }) => {
    const imageUrl = businessImages[business.place_id];
    const isLoadingImage = loadingImages[business.place_id];

    return (
      <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden flex flex-col border-b-4" style={{ borderBottomColor: "#7B189F", height: '100%' }}>
        <div className="flex flex-1 min-h-0">
          <div className="relative bg-gradient-to-br from-orange-400 to-red-500 flex-shrink-0" style={{
            width: 'clamp(80px, 6.94vw, 96px)',
            minWidth: '80px',
            maxWidth: '96px',
            height: '100%'
          }}>
            {business.rating && (
              <div className="absolute top-2 right-2 bg-green-500 text-white px-1.5 py-0.5 rounded text-xs font-bold flex items-center gap-1 z-10">
                <Star className="w-2.5 h-2.5 fill-white" />
                {business.rating}
              </div>
            )}
            
            {isLoadingImage ? (
              <div className="w-full h-full flex items-center justify-center bg-gray-200">
                <div className="w-5 h-5 border-2 border-[#7B189F] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : imageUrl ? (
              <img 
                src={imageUrl} 
                alt={business.name}
                className="w-full h-full object-cover"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            
            {!imageUrl && !isLoadingImage && (
              <div className="w-full h-full flex items-center justify-center text-white text-xs opacity-50">
                No IMG
              </div>
            )}
          </div>
          <div className="p-3 flex-1 flex flex-col justify-between min-w-0">
            <div>
              <h4 className="font-bold text-sm text-gray-800 mb-1 line-clamp-1">
                {business.name}
              </h4>
              <p className="text-gray-600 text-xs mb-2 line-clamp-1">
                {business.category}
              </p>
            </div>
            <div className="flex items-center justify-between text-xs text-gray-500 flex-shrink-0">
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">
                  {business.vicinity?.slice(0, 25) +
                    (business.vicinity?.length > 25 ? "…" : "")}
                </span>
              </div>
              <span className="font-medium ml-2 flex-shrink-0">{business.distance} km</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const MobileCard = ({ business }) => {
    const imageUrl = businessImages[business.place_id];
    const isLoadingImage = loadingImages[business.place_id];

    return (
      <div className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden">
        <div className="flex" style={{ height: 'clamp(80px, 6.94vw, 96px)', minHeight: '80px' }}>
          <div className="relative bg-gradient-to-br from-orange-400 to-red-500 flex-shrink-0" style={{
            width: 'clamp(80px, 6.94vw, 96px)',
            minWidth: '80px',
            maxWidth: '96px',
            height: '100%'
          }}>
            {business.discount && (
              <div className="absolute top-1 left-1 bg-blue-600 text-white px-1.5 py-0.5 rounded text-xs font-medium z-10">
                10%
              </div>
            )}
            {business.rating && (
              <div className="absolute top-2 right-2 bg-green-500 text-white px-1.5 py-0.5 rounded text-xs font-bold flex items-center gap-1 z-10">
                <Star className="w-2.5 h-2.5 fill-white" />
                {business.rating}
              </div>
            )}
            
            {isLoadingImage ? (
              <div className="w-full h-full flex items-center justify-center bg-gray-200">
                <div className="w-5 h-5 border-2 border-[#7B189F] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : imageUrl ? (
              <img 
                src={imageUrl} 
                alt={business.name}
                className="w-full h-full object-cover"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            
            {!imageUrl && !isLoadingImage && (
              <div className="w-full h-full flex items-center justify-center text-white text-xs opacity-50">
                No IMG
              </div>
            )}
          </div>
          <div className="p-3 flex-1 flex flex-col justify-between min-w-0">
            <div>
              <h4 className="font-bold text-base text-gray-800 mb-1 line-clamp-1">
                {business.name}
              </h4>
              <p className="text-gray-600 text-sm mb-2 line-clamp-1">{business.category}</p>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-500 flex-shrink-0">
              <div className="flex items-center gap-1 flex-1 min-w-0">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">
                  {business.vicinity?.slice(0, 25) +
                    (business.vicinity?.length > 25 ? "…" : "")}
                </span>
              </div>
              <span className="font-medium ml-2 flex-shrink-0">{business.distance} km</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto" style={{ 
      maxWidth: '1920px', 
      margin: '0 auto',
      paddingTop: 'clamp(96px, 8.33vw, 160px)',
      paddingBottom: 'clamp(96px, 8.33vw, 160px)',
      paddingLeft: 'clamp(16px, 1.25vw, 24px)',
      paddingRight: 'clamp(16px, 1.25vw, 24px)'
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@400;700&family=Afacad:wght@400;500;600&display=swap');
        .fraunces-bold {
          font-family: 'Fraunces', serif;
          font-weight: 700;
        }
      `}</style>
      {/* Header */}
      <div className="text-center" style={{ marginBottom: 'clamp(32px, 2.78vw, 54px)' }}>
        <h1 className="fraunces-bold text-[#7B189F]" style={{ 
          fontSize: 'clamp(18px, 1.31vw, 26px)', 
          fontWeight: 700,
          lineHeight: '1.2',
          marginBottom: 'clamp(12px, 0.83vw, 16px)'
        }}>
          Discover
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto fraunces-bold" style={{ 
          fontSize: 'clamp(16px, 1.11vw, 22px)', 
          fontWeight: 700,
          lineHeight: '1.4',
          marginTop: 0
        }}>
          Find local businesses, restaurants, and services near you
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="mb-8 overflow-x-auto scrollbar-hide">
        <div className="flex justify-center gap-3 md:gap-4 min-w-max px-4 md:px-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab)}
              disabled={loading}
              className={`flex items-center gap-2 px-4 py-2 md:px-6 md:py-3 rounded-full transition-all whitespace-nowrap text-sm md:text-base ${
                activeTab.id === tab.id
                  ? "bg-[#7B189F] text-white shadow-lg"
                  : "bg-white text-gray-600 border border-gray-300 hover:border-[#7B189F]"
              } ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              {tab.imagePath ? (
                <img
                  src={tab.imagePath}
                  alt={tab.label}
                  className={`w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 transition-all ${
                    activeTab.id === tab.id
                      ? "brightness-0 invert"
                      : "brightness-0"
                  }`}
                  style={{
                    filter: activeTab.id === tab.id 
                      ? "brightness(0) invert(1)" // White when active
                      : "brightness(0)" // Black when inactive
                  }}
                />
              ) : tab.icon ? (
                <span>{tab.icon}</span>
              ) : null}
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center min-h-[500px]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#7B189F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg font-medium">
              Loading {activeTab.label.toLowerCase()}...
            </p>
            <p className="text-gray-500 text-sm mt-2">
              Finding the best places near you
            </p>
          </div>
        </div>
      )}


      {/* Content */}
      {!loading && currentData.length > 0 && (
        <>
          {/* Mobile Layout - Vertical Stack */}
          <div className="md:hidden flex flex-col gap-4 mb-8">
            {currentData.slice(0, 4).map((business, index) => (
              <MobileCard key={business.place_id || index} business={business} />
            ))}
          </div>

          {/* Desktop Layout - Hidden on mobile */}
          <div className="hidden md:flex gap-6 mb-8" style={{ minHeight: 'clamp(300px, 26.04vw, 400px)' }}>
            {/* Featured Card */}
            <div className="w-1/3 flex" style={{ minHeight: 'clamp(300px, 26.04vw, 400px)' }}>
              {currentData[0] && <FeaturedCard business={currentData[0]} />}
            </div>

            {/* Small Cards Grid */}
            <div className="flex-1">
              <div className="grid grid-cols-2 gap-4" style={{ height: '100%' }}>
                {currentData.slice(1, 7).map((business, index) => (
                  <div key={business.place_id || index} style={{ height: 'clamp(140px, 12.15vw, 190px)', minHeight: '140px' }}>
                    <SmallCard business={business} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Additional Cards - Show more on desktop */}
          {currentData.length > 7 && (
            <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
              {currentData.slice(7).map((business, index) => (
                <SmallCard key={business.place_id || index} business={business} />
              ))}
            </div>
          )}

          {/* Additional Cards - Mobile */}
          {currentData.length > 4 && (
            <div className="md:hidden flex flex-col gap-4 mb-8">
              {currentData.slice(4).map((business, index) => (
                <MobileCard key={business.place_id || index} business={business} />
              ))}
            </div>
          )}
        </>
      )}

      {/* No Data State */}
      {!loading && currentData.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">🔍</div>
          <p className="text-gray-600 text-lg mb-2 font-medium">
            No {activeTab.label.toLowerCase()} found nearby
          </p>
          <p className="text-gray-500 text-sm">
            Try selecting a different category or adjust your location settings
          </p>
        </div>
      )}

      {/* Load More Button */}
      {!loading && currentData.length > 0 && displayCount < mapData.length && (
        <div className="text-center mt-8">
          <button
            onClick={handleLoadMore}
            className="hover:opacity-80 transition-opacity inline-flex items-center justify-center gap-2"
            style={{ 
              color: "#411B26",
              fontSize: 'clamp(12px, 0.83vw, 16px)',
              fontWeight: 600,
              fontFamily: 'Afacad, sans-serif'
            }}
          >
            Load More
            <img
              src="/landing/more.png"
              alt="More"
              style={{ width: 'clamp(30px, 2.17vw, 43px)', height: 'clamp(30px, 2.17vw, 43px)' }}
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default DiscoverSection;